import { Card, Flex, Button, Text, Badge, Box } from '@chakra-ui/react';
import { createDataCollectionContext, dbTimeToUserTime, useDrawer, useMobileContext } from '@shared';
import { useTranslation } from 'react-i18next';
import { createColumnHelper } from '@tanstack/react-table';
import inboundNumberReservationsService from '../../api/services/inboundNumberReservationsService';
import { useMemo } from 'react';
import SuperadminInboundNumberReservationDrawer from './SuperadminInboundNumberReservationDrawer';
import { useGetAllInboundNumbers } from '../../api/hooks/useInboundNumberSuperadmin';
import { useGetAllAccounts } from '../../api/hooks/useAccountSuperadmin';

const columnHelper = createColumnHelper<InboundNumberReservation>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function SuperadminInboundNumberReservations() {
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<InboundNumberReservation>(),
		[],
	);

	const { isMobile } = useMobileContext();

	const { t } = useTranslation();
	const { data: inboundNumbers } = useGetAllInboundNumbers();
	const { data: accounts } = useGetAllAccounts();

	const { openDrawer } = useDrawer();

	function getIsActiveTranslation(isActive: boolean): string {
		switch (isActive) {
			case true:
				return t('general.yes');

			default:
				return t('general.no');
		}
	}

	function getBadgeColorByStatus(isActive: boolean): string {
		switch (isActive) {
			case true:
				return 'green';

			default:
				return 'yellow';
		}
	}

	const columns = [
		columnHelper.accessor('inboundNumberReservationID', {
			header: t('general.id'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('accountID', {
			header: t('general.account'),
			enableSorting: false,
			cell: ({ row }) => {
				const account = accounts?.data.accounts.find(ac => ac.accountID === row.original.accountID);
				return <Text>{account?.name}</Text>;
			},
		}),
		columnHelper.accessor('inboundNumberID', {
			header: t('general.phone'),
			enableSorting: false,
			cell: ({ row }) => {
				const inboundNumber = inboundNumbers?.data.inboundNumbers.find(number => number.inboundNumberID === row.original.inboundNumberID);
				return <Text>{inboundNumber?.phone}</Text>;
			},
		}),
		columnHelper.accessor('isActive', {
			header: t('general.active'),
			enableSorting: false,
			cell: ({ row }) => {
				return (
					<Badge size="md" colorPalette={getBadgeColorByStatus(row.original.isActive)}>
						{getIsActiveTranslation(row.original.isActive)}
					</Badge>
				);
			},
		}),
		columnHelper.accessor('timeStart', {
			header: t('general.time_active_start'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeStart, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.accessor('timeEnd', {
			header: t('general.time_active_end'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (row.original.timeEnd) {
					return <Text>{dbTimeToUserTime(row.original.timeEnd, TIMEZONE)}</Text>;
				}
				return '-';
			},
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
	];

	return (
		<Card.Root flex={1} display="flex">
			<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
				<DataCollectionContextProvider
					dataKey="inboundNumberReservations"
					keyAttribute="inboundNumberReservationID"
					emptyDataText={t('general.no_inbound_number_reservations_found')}
					fetchFn={(...args) => inboundNumberReservationsService.getAllResources(...args)}
					initialItemsPerPage={25}
					columns={columns}
					defaultHiddenColumns={[]}
					isServerSide={true}
					isSelectable={false}
					defaultSort={{ id: 'timeCreated', desc: true }}
					onClickRow={row => {
						openDrawer({
							id: 'inboundNumberReservationDrawer',
							content: <SuperadminInboundNumberReservationDrawer inboundNumberReservationID={row.original.inboundNumberReservationID} />,
						});
					}}
				>
					<Flex gap={2} flexWrap="wrap" flexShrink={0} ml="auto">
						<DataCollectionTableHeader />
						<Button
							id="addInboundNumberReservation"
							onClick={() => {
								openDrawer({
									id: 'inboundNumberReservationDrawer',
									content: <SuperadminInboundNumberReservationDrawer />,
								});
							}}
						>
							{t('general.add_inbound_number_reservation')}
						</Button>
					</Flex>
					<Box flex={1} overflow="auto" minH={0}>
						<DataCollectionTable />
					</Box>
					<Flex gap={2} flexShrink={0}>
						<DataCollectionTableFooter />
					</Flex>
				</DataCollectionContextProvider>
			</Card.Body>
		</Card.Root>
	);
}
